<!-- @format -->

<template>
	<div>
		<slot :params="checkValue" v-bind="$attrs" @click="onShowDialog">
			<el-select
				v-model="checkIds"
				multiple
				:teleported="false"
				class="selectRoleClass"
				popper-class="selectRolePopper"
				placeholder="请选择角色"
				style="width: 100%"
				v-bind="$attrs"
				@click="onShowDialog"
				@remove-tag="(e) => onDelete(e)"
			>
				<el-option v-for="item in checkValue" :key="item.roleId" :label="item.roleName" :value="item.roleId" />
			</el-select>
		</slot>
		<pc-dialog
			v-model="visible"
			append-to-body
			:close-on-click-modal="false"
			title="选择角色"
			width="800px"
			top="5vh"
			v-bind="dialogProps"
			@on-cancel="onCancel"
			@on-confirm="onConfirm"
		>
			<slot name="detail" :params="checkValue"></slot>
			<el-form :model="queryParams" :inline="true" @submit.prevent>
				<el-form-item label="角色名称" prop="roleName">
					<el-input
						v-model="queryParams.roleName"
						placeholder="请输入用户账号"
						clearable
						style="width: 240px"
						@keyup.enter="onSearch"
					/>
				</el-form-item>
			</el-form>

			<pc-table
				ref="tableRef"
				v-loading="loading"
				:data="roleList.list"
				max-height="360px"
				:class="['content', !multiple && 'content-once']"
				:total="roleList.total"
				name="rolePicker"
				@page-change="onSearch"
				@select="onSelect"
				@select-all="onSelectAll"
			>
				<el-table-column type="selection" width="55"></el-table-column>
				<el-table-column label="角色名称" prop="roleName" :show-overflow-tooltip="true" />
				<el-table-column label="权限字符" prop="roleKey" :show-overflow-tooltip="true" />
				<el-table-column label="角色状态" prop="status" :show-overflow-tooltip="true">
					<template #default="scope">
						<el-switch :model-value="scope.row.status === '0'" />
					</template>
				</el-table-column>
			</pc-table>
		</pc-dialog>
	</div>
</template>

<script setup lang="ts">
	import { useRequest } from '@power/runtime-vue3';
	import { PcDialog, PcTable } from '@/components';
	import { getRoleList } from '@/api/user';

	interface Props {
		modelValue: (string | number)[];
		dialogProps?: Record<string, any>;
		multiple?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {});

	const emit = defineEmits(['update:modelValue', 'onConfirm', 'onSelect']);

	const attrs = useAttrs();

	const tableRef = ref<InstanceType<typeof PcTable>>();

	const visible = ref<boolean>(false);

	const roleList = ref<{
		list: any[];
		total: number;
	}>({
		list: [],
		total: 0,
	});

	const queryParams = ref({
		pageNum: 1,
		pageSize: 10,
		roleName: undefined,
		orderByColumn: 'roleName',
		isAsc: 'descending',
	});

	// 选中的ids
	const checkIds = computed({
		get() {
			return props.modelValue || [];
		},
		set(val) {
			emit('update:modelValue', val);
		},
	});

	// 选中的带有label、value的数组
	const checkValue = ref<any[]>([]);

	// 选中的id缓存，点确定时才将数据给checkIds
	const cacheCheckValue = ref<any[]>([]);

	const onShowDialog = () => {
		if (!attrs.disabled) {
			visible.value = true;
			cacheCheckValue.value = JSON.parse(JSON.stringify(checkValue.value));
			getList();
		}
	};

	const { loading, load: getListLoad } = useRequest(getRoleList);

	const getList = () => {
		getListLoad({ ...queryParams.value }).then((res) => {
			//console.log(res);
			roleList.value = {
				list: res.rows,
				total: res.total,
			};
			nextTick(() => {
				setRowSelection(cacheCheckValue.value.map((i) => i.roleId));
			});
		});
	};

	const setRowSelection = (checkIds) => {
		roleList.value.list.forEach((row) => {
			if (checkIds.includes(row.roleId)) {
				tableRef.value?.ref?.toggleRowSelection(row, true);
			}
		});
	};

	const onDelete = (e) => {
		checkIds.value = checkIds.value.filter((i) => i !== e);
		checkValue.value = checkValue.value.filter((i) => i.roleId !== e);
	};

	const onSearch = (e = {}) => {
		queryParams.value = { ...queryParams.value, pageNum: 1, ...(e || {}) };
		getList();
	};

	const onConfirm = (callback) => {
		visible.value = false;
		const roleIds = cacheCheckValue.value.map((i) => i.roleId);
		checkIds.value = roleIds;
		checkValue.value = toRaw(cacheCheckValue.value);
		emit('onConfirm', roleIds, cacheCheckValue);
		callback();
	};

	const onCancel = (callback) => {
		visible.value = false;
		callback();
	};

	const onSelect = (selection, row) => {
		if (props.multiple) {
			const index = cacheCheckValue.value.findIndex((i) => i.roleId === row.roleId);
			index !== -1 ? cacheCheckValue.value.splice(index, 1) : cacheCheckValue.value.push(row);
		} else {
			tableRef.value?.ref?.clearSelection();
			tableRef.value?.ref?.toggleRowSelection(selection.pop(), true);
			cacheCheckValue.value = [row];
		}
		emit('onSelect', cacheCheckValue);
	};

	const onSelectAll = (rows) => {
		if (rows.length) {
			const list = rows.filter((i) => !cacheCheckValue.value.some((it) => it.roleId === i.roleId));
			cacheCheckValue.value = [...cacheCheckValue.value, ...list];
		} else {
			const list = cacheCheckValue.value.filter((i) => !roleList.value.list.some((itm) => itm.roleId === i.roleId));
			cacheCheckValue.value = list;
		}
		emit('onSelect', cacheCheckValue);
	};

	onMounted(() => {
		// 获取对应label展示用，暂时用调接口方式
		checkIds.value.length &&
			getListLoad({ pageSize: 10000, pageNum: 1 }).then((res) => {
				const list = res.rows.filter((i) => checkIds.value.includes(i.roleId));
				checkValue.value = list;
			});
	});

	defineExpose({ tableRef: tableRef.value?.ref, checkRows: checkValue });
</script>

<style lang="scss" scoped>
	.content {
		border: 1px solid var(--el-border-color);
		padding: 15px;
		border-radius: 5px;
	}

	.content-once {
		:deep(.el-table-column--selection.is-leaf .el-checkbox) {
			display: none;
		}
	}

	.selectRoleClass {
		:deep(.selectRolePopper) {
			visibility: hidden;
		}
	}
</style>
